import { JSONObject } from '@lucidtech/las-sdk-browser';
import { memo, useCallback, useState } from 'react';

import {
  Button,
  Dialog,
  Heading,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Modal,
  ModalOverlay,
  ModalOverlayProps,
  Popover,
  Select,
  SelectValue,
  Text,
  TextField,
} from '@/components/core';
import { ChevronIcon, CloseIcon, InfoIcon } from '@/components/icons';
import { useModelConfigStore } from '@/store';
import { OmitChildren } from '@/utils';

import { findField } from './utils';

export type ConnectWithURLDialogProps = OmitChildren<ModalOverlayProps> & {
  fieldId: string;
  formatterId: string;
  onConnect: (httpConfig: JSONObject) => void;
  onClose?: () => void;
};

const IConnectWithURLDialog = ({
  fieldId,
  formatterId,
  onConnect: onConnectFn,
  onClose,
  ...rest
}: ConnectWithURLDialogProps) => {
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));
  const formatter = field?.formatters?.find((f) => f.id === formatterId);
  const [url, setUrl] = useState<string | undefined>(formatter?.config?.httpConfig?.url);
  const [httpMethod, setHttpMethod] = useState<string>(formatter?.config?.httpConfig?.httpMethod ?? 'GET');

  const onConnect = useCallback(() => {
    if (url == null) return;

    const httpConfig: JSONObject = { url, httpMethod };
    onConnectFn(httpConfig);
  }, [httpMethod, onConnectFn, url]);

  return (
    <ModalOverlay {...rest}>
      <Modal>
        <Dialog className="min-w-[24rem]">
          <div className="flex items-center border-b border-gray-200 p-4">
            <div className="item-center gap-xs flex flex-col">
              <Heading>Connect with URL</Heading>
              <Text>Connect to a dynamic table using webhook</Text>
            </div>

            <div className="grow" />

            <Button variant="plain" onPress={onClose}>
              <CloseIcon />
            </Button>
          </div>

          <div className="gap-sm flex flex-col p-4">
            <div className="gap-sm flex">
              <TextField className="grow" value={url} onChange={setUrl}>
                <Label>URL</Label>
                <Input />
              </TextField>

              <Select
                className="mt-5"
                onChange={(value) => {
                  if (value) setHttpMethod(value.toString());
                }}
                value={httpMethod}
              >
                <Button variant="secondary">
                  <SelectValue />
                  <ChevronIcon rotation="down" />
                </Button>

                <Popover placement="right">
                  <ListBox>
                    <ListBoxItem id="GET">
                      <Text>GET</Text>
                    </ListBoxItem>
                    <ListBoxItem id="POST">
                      <Text>POST</Text>
                    </ListBoxItem>
                  </ListBox>
                </Popover>
              </Select>
            </div>
          </div>

          <div className="gap-sm flex items-center p-4">
            <div className="grow" />

            <Button variant="plain" onPress={onClose}>
              <Text>Cancel</Text>
            </Button>
            <Button isDisabled={url == null} variant="primary" onPress={onConnect}>
              <Text>Connect</Text>
            </Button>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};

export const ConnectWithURLDialog = memo(IConnectWithURLDialog) as typeof IConnectWithURLDialog;
